import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./SelectSources.module.css";
import logo from "../../assets/images/LightLogo.webp";
import { HiArrowLeft } from "react-icons/hi2";
import { Card, Col, Image, Row, Switch, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { selectSourceData } from "../../redux/slices/sourceSlice";
import { AppDispatch } from "../../redux/store";
import { selectAuthenticated } from "../../redux/slices/authSlice";
import { loginAnonymously } from "../../redux/actions/auth";
import { fetchSourcesByCountry } from "../../redux/actions/sourcesActions";
import { resetNews } from "../../redux/slices/newsSlice";

const SelectSources: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectAuthenticated);
  const sourcesData = useSelector(selectSourceData);

  const [country, setCountry] = useState<string>(
    localStorage.getItem("country") || "",
  );

  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);

  useEffect(() => {
    const fetchData = () => dispatch(fetchSourcesByCountry());

    if (isAuthenticated) {
      fetchData();
    } else {
      dispatch(loginAnonymously()).then(fetchData).catch(console.error);
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (!sourcesData?.content?.length) return;

    const saved = localStorage.getItem(`selectedSources_${country}`);

    if (saved) {
      setSelectedCards(JSON.parse(saved));
      setSelectAll(localStorage.getItem(`allSelected_${country}`) === "true");
    } else {
      const allIds = sourcesData.content.map((card) => card.id);
      setSelectedCards(allIds);
      setSelectAll(true);
      localStorage.setItem(
        `selectedSources_${country}`,
        JSON.stringify(allIds),
      );
      localStorage.setItem(`sourceIds_${country}`, allIds.join(","));
      localStorage.setItem(`allSelected_${country}`, "true");
    }
  }, [sourcesData, country]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 425);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCardClick = (id: string) => {
    const isSelected = selectedCards.includes(id);
    let updatedSelection: string[];

    if (isSelected) {
      updatedSelection = selectedCards.filter((cardId) => cardId !== id);
      setSelectAll(false);
    } else {
      updatedSelection = [...selectedCards, id];
      if (updatedSelection.length === sourcesData.content.length) {
        setSelectAll(true);
      }
    }

    setSelectedCards(updatedSelection);
    localStorage.setItem(
      `selectedSources_${country}`,
      JSON.stringify(updatedSelection),
    );
    localStorage.setItem(`sourceIds_${country}`, updatedSelection.join(","));
    localStorage.setItem(
      `allSelected_${country}`,
      updatedSelection.length === sourcesData.content.length ? "true" : "false",
    );
  };

  const handleSelectAll = () => {
    if (!sourcesData?.content?.length) return;

    if (selectAll) {
      setSelectedCards([]);
      setSelectAll(false);
      localStorage.setItem(`selectedSources_${country}`, JSON.stringify([]));
      localStorage.setItem(`sourceIds_${country}`, "");
      localStorage.setItem(`allSelected_${country}`, "false");
    } else {
      const allIds = sourcesData.content.map((card) => card.id);
      setSelectedCards(allIds);
      setSelectAll(true);
      localStorage.setItem(
        `selectedSources_${country}`,
        JSON.stringify(allIds),
      );
      localStorage.setItem(`sourceIds_${country}`, allIds.join(","));
      localStorage.setItem(`allSelected_${country}`, "true");
    }
  };

  const onContinueHandler = () => {
    if (selectedCards.length === 0) {
      notification.warning({
        message: "Ju lutem zgjedhni të paktën një kanal",
        placement: "bottomRight",
        duration: 2,
      });
      return;
    }

    dispatch(resetNews());
    navigate("/lajmet");

    notification.success({
      message: "Ruajtur me sukses!",
      placement: "bottomRight",
      duration: 1,
      className: "customNotification",
    });
  };

  if (!sourcesData?.content?.length) {
    return (
      <div className={classes.mainDiv}>
        <p style={{ color: "white", textAlign: "center", marginTop: "2rem" }}>
          Po ngarkohen kanalet...
        </p>
      </div>
    );
  }

  return (
    <div className={classes.mainDiv}>
      <Row>
        <Col span={24} className={classes.headerDiv}>
          <div
            className={classes.backTitle}
            onClick={() => navigate("/selectstate")}
          >
            <div className={classes.circleButton}>
              <HiArrowLeft fontSize="1.2rem" color="white" />
            </div>
            <h2>Rikthehuni</h2>
          </div>
        </Col>
      </Row>

      <Row className={classes.centerDiv}>
        <Col span={18}>
          <div className={classes.centerDivHeader}>
            <div className={classes.headerLogo}>
              <img src={logo} alt="logoImage" className={classes.stateLogo} />
            </div>
            <h2>Top Kanalet</h2>
            <h3>Zgjidhni kanalet tuaja të preferuara.</h3>
          </div>
        </Col>

        <Col span={18} className={classes.toggleSwitchCol}>
          <div className={classes.toggleSwitch}>
            <h3>Të gjitha</h3>
            <Switch checked={selectAll} onChange={handleSelectAll} />
          </div>
        </Col>

        <Col span={18} className={classes.selectSourcesDiv}>
          {sourcesData.content.map(
            (item, index) =>
              index % 3 === 0 && (
                <Row gutter={[16, { xs: 0, sm: 0, md: 8 }]} key={index}>
                  {sourcesData.content.slice(index, index + 3).map((card) => (
                    <Col xs={24} sm={24} md={8} key={card.id}>
                      <Card
                        onClick={() => handleCardClick(card.id)}
                        className={classes.sourceCard}
                        style={{
                          marginBottom: "16px",
                          backgroundColor: selectedCards.includes(card.id)
                            ? "#2D60E3"
                            : "#242424",
                          borderRadius: "0.2rem",
                          border: "none",
                          height: "3rem",
                          padding: "1rem",
                          transition: "background-color 0.3s",
                          cursor: "pointer",
                        }}
                        bodyStyle={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          padding: "10px",
                        }}
                      >
                        <div className={classes.sourceSection}>
                          <Image
                            preview={false}
                            width={"2rem"}
                            height={"2rem"}
                            src={card.sourceImage}
                            style={{
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                          <h4 style={{ color: "white" }}>{card.name}</h4>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ),
          )}
        </Col>
      </Row>

      <Row className={classes.confirmSection}>
        <Col span={6}>
          <Card
            onClick={onContinueHandler}
            className={classes.confirmCard}
            bodyStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: "10px",
            }}
          >
            <div className={classes.confirmButton}>
              <h4 style={{ color: "white" }}>Vazhdoni</h4>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SelectSources;
